import { useMemo, useEffect } from 'react';
import { useLocation, Navigate } from 'react-router-dom';

import {
  useOrder,
  SET_ORDER_DETAILS,
  SET_PRODUCTS,
} from '../../hooks/useOrder';
import OrderPage from '../../components/payments/OrderPage';
import { useAuth } from '../../hooks/useAuth';
import { getProduct } from '../../helpers/products';
import { currency } from '../../utils';
import EnhancementPackageDetailsDisplay from '../../components/EnhancementPackageDetailsDisplay';
import MyOrderCard from '../../components/payments/MyOrderCard';

export default function OrderEnhancement() {
  const { state, dispatch, openOrder, loadOrder } = useOrder();
  const { orderStatus, products } = state;

  const { user } = useAuth();

  const location = useLocation();

  const orderDetails = useMemo(() => {
    let orderDetails = location.state?.orderDetails;
    if (!orderDetails) {
      const params = new URLSearchParams(window.location.search);
      return {
        orderId: params.get('orderId'),
        coreProductName: params.get('coreProductName'),
        fileId: params.get('fileId'),
        fileName: params.get('fileName'),
        packageName: params.get('packageName'),
        matchedRecords: params.get('matchedRecords'),
        packageDefinition: params.get('packageDefinition'),
      };
    }
    return orderDetails;
  }, [location.state]);

  const {
    orderId,
    coreProductName,
    fileId,
    fileName,
    packageName,
    matchedRecords,
    packageDefinition,
  } = orderDetails;

  useEffect(() => {
    dispatch({
      type: SET_ORDER_DETAILS,
      payload: orderDetails,
    });
  }, [orderDetails, dispatch]);

  useEffect(() => {
    if (coreProductName && orderStatus == null && !orderId) {
      const product = getProduct(orderDetails.coreProductName, user?.plan);
      dispatch({
        type: SET_PRODUCTS,
        payload: [{ ...product, quantity: orderDetails.matchedRecords }],
      });
    }
  }, [
    coreProductName,
    dispatch,
    orderDetails,
    orderId,
    orderStatus,
    user?.plan,
  ]);

  useEffect(() => {
    if (orderId && orderStatus == null) {
      loadOrder({
        variables: {
          orderId,
          expandInvoice: true,
        },
      });
    }
  }, [loadOrder, orderId, orderStatus]);

  useEffect(() => {
    if (products?.length > 0 && orderStatus == null && !orderId) {
      openOrder({
        variables: {
          products,
          orderType: 'enhancement',
          orderDetails: {
            seedFile: fileId,
            seedFileName: fileName,
            packageName,
            recordsIncluded: matchedRecords,
            packageDefinition,
          },
        },
      });
    }
  }, [
    fileId,
    fileName,
    matchedRecords,
    openOrder,
    orderId,
    orderStatus,
    packageName,
    products,
    packageDefinition,
  ]);

  if (
    !coreProductName ||
    !fileId ||
    !fileName ||
    !matchedRecords ||
    !packageName
  ) {
    console.log('navigating');
    return <Navigate to="/dashboard/mywork/files" />;
  }

  return (
    <OrderPage
      productLabel={<ProductLabel fileName={fileName} />}
      productPricing={null}
      productDetails={null}
      orderSummary={<ProductDetails packageName={packageName} />}
      backButton={null}
    />
  );
}

function ProductLabel({ fileName }) {
  return (
    <p>
      Enhancement File/Audience Name: <strong className="font-bold">{fileName}</strong>
    </p>
  );
}

function ProductPricing() {
  const { state } = useOrder();
  const { orderDetails, products } = state;
  const { matchedRecords, coreProductName } = orderDetails;

  const { user } = useAuth();
  const { plan } = user;

  const id = useMemo(() => {
    if (coreProductName) {
      return getProduct(coreProductName, plan)?.id;
    }
  }, [coreProductName, plan]);

  const foundProduct = products?.find((p) => p.id === id);

  let price = foundProduct?.amount;
  let discount = foundProduct?.discount;

  return (
    <>
      <p>
        Matched Records:
        <span className="font-bold ml-2">
          {matchedRecords?.toLocaleString(undefined, {
            maximumFractionDigits: 0,
          })}
        </span>
      </p>
      <p>
        List Price:{' '}
        <span className="font-bold ml-2">
          {' '}
          {discount ? (
            <>
              <span className="line-through text-gray-400 mr-1">
                {currency(price)}
              </span>
              {currency(price - discount)}
            </>
          ) : (
            currency(price)
          )}
        </span>
      </p>
    </>
  );
}

function ProductDetails({ packageName }) {
  return (
    <div className="flex flex-col p-2 w-full" style={{ minWidth: 300 }}>
      <div className="mb-2">
        <h4 className="mb-0">My Order</h4>
      </div>
      <div className="flex flex-col bg-gray-100 rounded-half p-4 space-y-4">
        <MyOrderCard>
          <div className="mt-3">
            <EnhancementPackageDetailsDisplay packageName={packageName} />
          </div>
        </MyOrderCard>
      </div>
    </div>
  );
}
